import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './accountModal.module.scss';
import { Field, Form, Formik } from 'formik';
import { Button } from '@mui/material';
import { salonManagementActions } from 'Containers/SalonManagement/store';
import TextField from 'Containers/Common/TextField';
import { Armchair } from 'lucide-react';
import * as yup from 'yup';
import InputSelector from 'Containers/Common/CustomSelector/InputSelector';
import { getWorkers } from 'Containers/SalonManagement/store/selectors';
import { getIconUrl, imageEntities } from 'utils/ImagesCreator';
import SectionsButtonSelector from '../SectionsButtonSelector';
import { useSections } from '../constants';
import { getAccountsWorkers } from 'Containers/Settings/store/selectors';
import { settingsActions } from 'Containers/Settings/store';
import { toastActions } from 'Containers/Toasts/store';

const PRIVATE_APP_SECTION_ID = 10;

export default ({ closeModal, account }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const workers = useSelector(getWorkers());
  const accountsWorkers = useSelector(getAccountsWorkers());
  const { SUBSECTIONS } = useSections();

  useEffect(() => {
    if (!workers.length) {
      dispatch(salonManagementActions.GET_WORKERS.REQUEST());
    }
  }, [dispatch, workers.length]);

  const validationSchema = {
    email: yup
      .string()
      .email(t('settings.accountModal.fields.email.validation.validEmail'))
      .required(t('settings.accountModal.fields.email.validation.required')),
    poltrona: yup
      .number()
      .required(t('settings.accountModal.fields.chair.validation.required')),
    settori: yup
      .array()
      .min(1, t('settings.accountModal.fields.sections.validation.selectAtLeastOne'))
      .required(t('settings.accountModal.fields.sections.validation.selectAtLeastOne')),
  };

  const initialValues = {
    email: account?.email || '',
    poltrona: account?.poltrona?.idpoltrona || '',
    settori: (account?.permessi || []).filter(
      (permesso) => Number(permesso.sezione) !== PRIVATE_APP_SECTION_ID,
    ),
  };

  const getWorkersOptions = () => {
    if (account?.idUtenteBusiness === undefined) {
      return workers
        .filter((w) => !accountsWorkers.includes(w.idpoltrona))
        .map((worker) => ({
          name: worker.nome_referente,
          value: worker.idpoltrona,
          icon: getIconUrl(worker.idpoltrona, imageEntities.worker),
        }));
    }
    return workers.map((worker) => ({
      name: worker.nome_referente,
      value: worker.idpoltrona,
      icon: getIconUrl(worker.idpoltrona, imageEntities.worker),
    }));
  };

  const handleClose = () => {
    closeModal();
    dispatch(
      toastActions.OPEN_TOAST.SUCCESS({
        type: 'worker',
        props: {
          title: t('settings.accountModal.toast.accountStaff'),
          message: account?.idUtenteBusiness
            ? t('settings.accountModal.toast.editSuccess')
            : t('settings.accountModal.toast.addSuccess'),
          warning: false,
        },
      }),
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={yup.object().shape(validationSchema)}
      onSubmit={(values) => {
        const req = {
          email: values.email,
          poltrona: values.poltrona,
          nome_referente: workers.find((w) => w.idpoltrona === values.poltrona)?.nome_referente,
          permessi: values.settori
            .filter((settore) => Number(settore.sezione) !== PRIVATE_APP_SECTION_ID)
            .map((settore) => {
              const permesso = { ...settore };
              delete permesso.fields;
              return permesso;
            }),
        };

        if (account?.idUtenteBusiness !== undefined && account?.idUtenteBusiness !== null) {
          dispatch(
            settingsActions.EDIT_ACCOUNT_STAFF.REQUEST(
              { idUtenteBusiness: account.idUtenteBusiness, ...req },
              handleClose,
            ),
          );
        } else {
          dispatch(settingsActions.ADD_ACCOUNT_STAFF.REQUEST(req, handleClose));
        }
      }}
    >
      {({ values, setFieldValue, touched, setFieldTouched, errors }) => (
        <Form className={styles.account_modal}>
          <div className={styles.body}>
            <div className={styles.field_block}>
              <Field
                name="email"
                id="email"
                label={t('settings.accountModal.fields.email.label')}
                component={TextField}
                height="50px"
                margin="0px"
                placeholder={t('settings.accountModal.fields.email.placeholder')}
                error={touched.email && errors.email}
              />
            </div>

            <div className={styles.field_block}>
              <InputSelector
                options={getWorkersOptions()}
                value={values.poltrona}
                setValue={(value) => {
                  setFieldValue('poltrona', value);
                  setFieldTouched('poltrona', false);
                }}
                placeholder={t('settings.accountModal.fields.chair.placeholder')}
                title={t('settings.accountModal.fields.chair.title')}
                icon={<Armchair size={18} strokeWidth={2.2} />}
                withIcon
                error={touched.poltrona && errors.poltrona}
                isEmptyMessage={t('settings.accountModal.fields.chair.emptyMessage')}
              />
            </div>

            <div className={styles.permissions_card}>
              <SectionsButtonSelector
                title={t('settings.accountModal.fields.sections.title')}
                sections={SUBSECTIONS}
                selectedList={values.settori || []}
                setSelectedList={(selected) => {
                  setFieldValue('settori', selected);
                  setFieldTouched('settori', true, false);
                }}
                error={touched.settori && errors.settori}
              />
            </div>
          </div>

          <hr />
          <div className={styles.footer}>
            <Button type="button" className={styles.ghost_button} onClick={closeModal}>
              {t('common.actions.cancel')}
            </Button>
            <Button type="submit" className={styles.confirm_button} variant="contained">
              {t('common.actions.save')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
